import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import Select from "react-select";
import { Input, message, Spin, Select as AntSelect, Modal } from "antd";
import { useNotification } from "../../context/NotificationContext";

import moment from "moment";
import { Search, Plus, Minus, Trash2, PlusCircle, ArrowLeft, Check, Printer, X } from "react-feather";
import axios from "axios";

// Reuse components from billing
import CategoryTabs from "./components/billing/CategoryTabs";
import SearchBar from "./components/billing/SearchBar";
import MenuItemGrid from "./components/billing/MenuItemGrid";
import { usePosContext } from "../../context/PosContext"; // Adjust path if needed

const { Option } = AntSelect;

// Treat a prop as "provided" only if it's actually present and meaningful.
// An empty array would otherwise short-circuit the fetch and leave the page empty.
const hasProvidedArray = (v) => Array.isArray(v) && v.length > 0;
const hasProvidedObject = (v) => v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length > 0;

const KOTGenerationPage = ({
  roomId,
  tableId,
  onClose,
  tableNo,
  menuItems: propMenuItems,
  categories: propCategories,
  settings: propSettings,
  tables: propTables,
  rooms: propRooms,
  setRooms: propSetRooms,
  setTables: propSetTables,
  addKOT: propAddKOT,
  calledFrom = "unknown",
  servers,
}) => {
  const { tables: contextTables, rooms: contextRooms, addKOT: contextAddKOT, setTables: contextSetTables, setRooms: contextSetRooms } = usePosContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState("All Products");
  const [kotItems, setKotItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedTable, setSelectedTable] = useState(tableId || null);
  const [selectedRoom, setSelectedRoom] = useState(roomId || null);
  const [selectedKOTIndex, setSelectedKOTIndex] = useState(-1);
  const [isComplementary, setIsComplementary] = useState(false);
  const [complementaryReference, setComplementaryReference] = useState("");
  const [isPageOpen, setIsPageOpen] = useState(true);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [waiterList, setWaiterList] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // saving spinner only
  const [isAddingNewKOT, setIsAddingNewKOT] = useState(false);

  // -- DATA STATE (seeded from props if provided) --
  const [menuItems, setMenuItems] = useState(hasProvidedArray(propMenuItems) ? propMenuItems : []);
  const [categories, setCategories] = useState(hasProvidedArray(propCategories) ? propCategories : []);
  const [settings, setSettings] = useState(hasProvidedObject(propSettings) ? propSettings : null);
  const { notify } = useNotification();

  // -- SINGLE COMBINED BOOT FLAG --
  // True until all required boot fetches have settled. While true, every
  // interactive control on the page is disabled and the user sees a clear
  // "Preparing KOT workspace..." overlay rather than a half-dead UI.
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState(null);

  const [waiter, setWaiter] = useState(null);
  const [kotNote, setKotNote] = useState("");

  // ----- Body scroll lock (unchanged behavior) -----
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("ns-kot-active");

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("ns-kot-active");
    };
  }, []);

  // Reset state when tableId or roomId changes (modal reopened)
  useEffect(() => {
    setKotItems([]);
    setKotNote("");
    setSelectedKOTIndex(-1);
    setIsComplementary(false);
    setComplementaryReference("");
    setWaiter(null);
    setSelectedWaiter(null);
    setIsAddingNewKOT(false);
    setIsLoading(false);
    setSelectedTable(tableId || null);
    setSelectedRoom(roomId || null);
  }, [tableId, roomId]);

  const waiterOptions = useMemo(() => {
    const source = Array.isArray(waiterList) && waiterList.length
      ? waiterList
      : Array.isArray(servers)
      ? servers
      : [];

    return source
      .filter(Boolean)
      .map((s) => {
        if (s?.value && s?.label) return s;

        const waiterId = s?.waiter_id || s?.id || s?.server_id || s?.value || s?.name || s;
        const waiterName = s?.name || s?.server_name || s?.label || String(s);

        return {
          value: waiterId,
          label: waiterName,
          waiter_id: waiterId,
          waiter_name: waiterName,
          phone: s?.phone || null,
          waiter_code: s?.waiter_code || null,
          status: s?.status || null,
        };
      });
  }, [waiterList, servers]);

  const kotReactSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
    menu: (base) => ({ ...base, zIndex: 2147483647 }),
    control: (base, state) => ({
      ...base,
      minHeight: 42,
      borderRadius: 14,
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
      fontSize: 13,
      fontWeight: 800,
    }),
    singleValue: (base) => ({ ...base, color: "#0f172a", fontWeight: 900 }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontWeight: 800 }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      fontWeight: state.isSelected ? 900 : 750,
      cursor: "pointer",
    }),
  };

  const tables = propTables || contextTables || [];
  const rooms = propRooms || contextRooms || [];
  const setTables = propSetTables || contextSetTables || (() => {});
  const setRooms = propSetRooms || contextSetRooms || (() => {});
  const addKOT = propAddKOT || contextAddKOT || (() => {});

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token = localStorage.getItem("token");

  // -----------------------------------------------------------------------
  // SINGLE BOOT PHASE: parallel-fetch everything we need and only flip the
  // page to interactive when (a) we got items (blocking) and (b) all four
  // requests have settled. Non-items failures show a toast but don't block.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!token) {
      // No token: nothing to fetch. Just stop the boot spinner.
      setBootLoading(false);
      return undefined;
    }

    let isMounted = true;
    const headers = { Authorization: `Bearer ${token}` };

    const itemsHandled = hasProvidedArray(propMenuItems);
    const categoriesHandled = hasProvidedArray(propCategories);
    const settingsHandled = hasProvidedObject(propSettings);

    const itemsPromise = itemsHandled
      ? Promise.resolve()
      : axios
          .get(`${apiUrl}/pos/items/listings`, { headers })
          .then((response) => {
            if (!isMounted) return;
            const rawItems = Array.isArray(response.data) ? response.data : Object.values(response.data);
            const activeItems = rawItems
              .filter((item) => item.is_active && typeof item === "object" && item.menu_id && item.name)
              .map((item) => ({
                menu_id: item.menu_id,
                name: item.name,
                category_name: item.category_name || "Uncategorized",
                price_standard: Number(item.price_standard) || 0,
              }));
            setMenuItems(activeItems);
          })
          .catch((err) => {
            // Items are blocking — set error so the error screen renders.
            const errorMessage = err.response?.data?.error || "Failed to fetch menu items";
            if (isMounted) setError(errorMessage);
          });

    const categoriesPromise = categoriesHandled
      ? Promise.resolve()
      : axios
          .get(`${apiUrl}/pos/categories/listings`, { headers })
          .then((response) => {
            if (!isMounted) return;
            const raw = Array.isArray(response.data) ? response.data : [];
            const activeCategories = raw.filter(
              (cat) => cat.is_active && typeof cat === "object" && cat.category_id
            );
            setCategories(activeCategories);
          })
          .catch((err) => {
            if (isMounted) {
              message.warning(err.response?.data?.error || "Failed to fetch categories");
            }
          });

    const settingsPromise = settingsHandled
      ? Promise.resolve()
      : axios
          .get(`${apiUrl}/setting-pos`, { headers })
          .then((response) => {
            if (!isMounted) return;
            const settingsData = response.data.settings || {};
            setSettings(settingsData);
          })
          .catch((err) => {
            if (isMounted) {
              message.warning(err.response?.data?.error || "Failed to fetch settings");
            }
          });

    const waitersPromise = axios
      .get(`${apiUrl}/pos/waiters/listings?name=`, { headers })
      .then((response) => {
        if (!isMounted) return;
        const waiters = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.waiters)
          ? response.data.waiters
          : [];
        setWaiterList(
          waiters.filter((w) => !w?.status || String(w.status).toLowerCase() === "active")
        );
      })
      .catch((err) => {
        console.warn("Unable to fetch waiter list:", err.response?.data || err.message);
        // Waiters failing is non-blocking — fall back to `servers` prop.
      });

    Promise.allSettled([itemsPromise, categoriesPromise, settingsPromise, waitersPromise]).then(() => {
      if (isMounted) setBootLoading(false);
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token]);

  // ----- Reflect URL query params if no onClose handler -----
  useEffect(() => {
    if (onClose) return;

    const params = new URLSearchParams(location.search);
    const roomIdFromQuery = params.get("room");
    const tableIdFromQuery = params.get("table");
    if (roomIdFromQuery) {
      setSelectedRoom(roomIdFromQuery);
      setSelectedTable(null);
    } else if (tableIdFromQuery) {
      setSelectedTable(tableIdFromQuery);
      setSelectedRoom(null);
    }
  }, [location.search, onClose]);

  const existingKOTs = useMemo(() => {
    let kots = [];

    if (calledFrom === "table" && selectedTable) {
      const table = tables.find((t) => t.id === selectedTable);
      kots = table?.kots;
    } else if (calledFrom === "room" && selectedRoom) {
      // FIX: the room AntSelect uses room.id as its value, so look up by id.
      // (The previous code matched against r.roomNumber, which silently failed
      // when selectedRoom held an id — so existing KOTs never appeared for rooms.)
      const room =
        rooms.find((r) => r.id === selectedRoom) ||
        rooms.find((r) => r.roomNumber === selectedRoom); // backward-compat fallback
      kots = room?.kots;
    }

    return Array.isArray(kots) ? kots : [];
  }, [calledFrom, selectedTable, selectedRoom, tables, rooms]);

  const displayedKOTs = useMemo(() => existingKOTs.slice(-5), [existingKOTs]);

  useEffect(() => {
    if (selectedKOTIndex >= 0 && selectedKOTIndex < displayedKOTs.length) {
      const selectedKOT = displayedKOTs[selectedKOTIndex];

      setKotItems([...(selectedKOT.items || [])]);
      setIsComplementary(selectedKOT.is_complementary || false);
      setComplementaryReference(
        selectedKOT.complementary_reference ||
          selectedKOT.complimentary_reference ||
          ""
      );
      setKotNote(selectedKOT.notes || "");

      const savedWaiter = selectedKOT.waiter_id || selectedKOT.waiter_name
        ? waiterOptions.find((w) =>
            String(w.value) === String(selectedKOT.waiter_id) ||
            String(w.waiter_id) === String(selectedKOT.waiter_id) ||
            String(w.label) === String(selectedKOT.waiter_name) ||
            String(w.waiter_name) === String(selectedKOT.waiter_name)
          ) || null
        : null;

      setWaiter(savedWaiter);
      setSelectedWaiter(savedWaiter);
      setIsAddingNewKOT(false);
    }
  }, [selectedKOTIndex, displayedKOTs, waiterOptions]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts during boot
      if (bootLoading) return;

      const activeElement = document.activeElement;
      const isInputFocused = activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA";

      if (e.key === "Escape") {
        handleDismiss();
      } else if (e.key === "Enter" && kotItems.length > 0 && !isInputFocused && selectedKOTIndex === -1) {
        handleGenerateKOT({
          kot_number: `KOT-${Date.now()}`,
          table_id: calledFrom === "table" ? selectedTable : null,
          room_id: calledFrom === "room" ? selectedRoom : null,
          items: kotItems,
          order_time: moment().format("YYYY-MM-DD HH:mm:ss"),
          is_complementary: isComplementary,
          complementary_reference: isComplementary ? complementaryReference : undefined,
          waiter_id: waiter ? waiter.value : undefined,
          waiter_name: waiter ? waiter.label : undefined,
          notes: settings?.kot_settings?.print_item_notes_in_kot ? kotNote : undefined,
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kotItems, selectedTable, selectedRoom, isComplementary, waiter, settings, selectedKOTIndex, kotNote, calledFrom, bootLoading, complementaryReference]);

  const displayedItems = useMemo(() => {
    let items = menuItems;
    if (selectedCategory !== "All Products") {
      items = items.filter((item) => item.category_name === selectedCategory);
    }
    return items;
  }, [selectedCategory, menuItems]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) {
      return displayedItems;
    }
    return displayedItems.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      return item.name.toLowerCase().includes(searchLower);
    });
  }, [searchTerm, displayedItems]);

  // Stable, memoised debounced setter — useMemo is correct here (returns the
  // SAME debounced fn forever), and we cancel on unmount.
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel?.();
    };
  }, [debouncedSetSearchTerm]);

  const handleAddItem = useCallback((item) => {
    const menuId = item?.menu_id || item?.item_id || item?.id;
    if (!menuId) {
      message.error("Unable to add item. Menu item id is missing.");
      return;
    }

    if (selectedKOTIndex >= 0) {
      setSelectedKOTIndex(-1);
    }

    setIsAddingNewKOT(true);
    setKotItems((prev) => {
      const existing = prev.find((i) => String(i.menu_id) === String(menuId));
      if (existing) {
        return prev.map((i) =>
          String(i.menu_id) === String(menuId)
            ? { ...i, qty: Number(i.qty || 0) + 1 }
            : i
        );
      }

      return [
        ...prev,
        {
          menu_id: menuId,
          name: item.name,
          qty: 1,
          price_standard: Number(item.price_standard || item.price || item.rate || 0),
        },
      ];
    });
  }, [selectedKOTIndex]);

  const handleQuantityChange = useCallback((menu_id, change) => {
    if (selectedKOTIndex >= 0) return;
    setKotItems((prev) =>
      prev.map((item) => {
        if (item.menu_id === menu_id) {
          const newQty = Math.max(0.1, item.qty + change);
          return { ...item, qty: newQty };
        }
        return item;
      })
    );
  }, [selectedKOTIndex]);

  const handleRemoveItem = useCallback((menu_id) => {
    if (selectedKOTIndex >= 0) return;
    setKotItems((prev) => prev.filter((item) => item.menu_id !== menu_id));
  }, [selectedKOTIndex]);



  const handleGenerateKOT = async (kot) => {
    if (kot?.is_complementary && !String(complementaryReference || "").trim()) {
      message.error("Please enter a complementary reference before generating KOT.");
      return;
    }

    setIsLoading(true);
    try {
      const finalKotNote = [
        kotNote,
        kot.is_complementary && complementaryReference
          ? `Complimentary Reference: ${complementaryReference}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");

      const payload = {
        kot_number: kot.kot_number,
        table_id: calledFrom === "table" ? kot.table_id : null,
        room_id: calledFrom === "room" ? kot.room_id : null,
        order_time: kot.order_time,
        items: kot.items.map((item) => ({
          menu_id: item.menu_id,
          name: item.name,
          qty: item.qty,
          price_standard: Number(item.price_standard) || 0,
        })),
        is_complementary: kot.is_complementary,
        complementary_reference: kot.is_complementary ? complementaryReference : undefined,
        ...(waiter ? { waiter_id: waiter.value, waiter_name: waiter.label } : {}),
        ...(finalKotNote ? { notes: finalKotNote } : {}),
      };

      const response = await axios.post(`${apiUrl}/pos/kots`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data || !response.data.kot_id) {
        throw new Error("Invalid response from server: KOT ID not found");
      }
      const { kot_id } = response.data;
      if (selectedKOTIndex >= 0 && selectedKOTIndex < displayedKOTs.length) {
        if (calledFrom === "table" && selectedTable) {
          setTables((prev) =>
            prev.map((table) =>
              table.id === selectedTable
                ? {
                    ...table,
                    kots: table.kots.map((k, index) =>
                      index === selectedKOTIndex ? { ...k, items: kotItems, is_complementary: isComplementary, complementary_reference: complementaryReference, waiter_id: waiter?.value, waiter_name: waiter?.label, notes: finalKotNote } : k
                    ),
                  }
                : table
            )
          );
        } else if (calledFrom === "room" && selectedRoom) {
          setRooms((prev) =>
            prev.map((room) =>
              // FIX: match by id to be consistent with the select value
              room.id === selectedRoom
                ? {
                    ...room,
                    kots: room.kots.map((k, index) =>
                      index === selectedKOTIndex ? { ...k, items: kotItems, is_complementary: isComplementary, complementary_reference: complementaryReference, waiter_id: waiter?.value, waiter_name: waiter?.label, notes: finalKotNote } : k
                    ),
                  }
                : room
            )
          );
        }
        notify("success",`KOT for ${selectedTable || selectedRoom} updated successfully!`);
      } else {
        const kotWithMeta = {
          ...kot,
          kot_id,
          complementary_reference: kot.is_complementary ? complementaryReference : undefined,
          waiter_id: waiter?.value,
          waiter_name: waiter?.label,
          notes: finalKotNote,
        };
        addKOT(selectedTable, selectedRoom, kotWithMeta);
        notify("success",`KOT for ${selectedTable || selectedRoom} generated successfully!`);
      }

      setKotItems([]);
      setKotNote("");
      setComplementaryReference("");
      setSelectedKOTIndex(-1);
      setIsComplementary(false);
      setWaiter(null);
      setIsAddingNewKOT(true);
    } catch (err) {
      console.error("Error generating KOT:", err);
      const errorMessage = err.response?.data?.error || "Failed to generate KOT";
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelKOT = (kotId) => {
    Modal.confirm({
      title: "Cancel KOT",
      zIndex: 2147483647,
      content: `Are you sure you want to cancel this KOT for ${selectedTable || selectedRoom}?`,
      okText: "Yes, Cancel",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await axios.patch(`${apiUrl}/pos/kots/${kotId}/cancel`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (calledFrom === "table" && selectedTable) {
            setTables((prev) =>
              prev.map((table) =>
                table.id === selectedTable
                  ? {
                      ...table,
                      kots: table.kots.map((k, index) =>
                        index === selectedKOTIndex ? { ...k, status: "cancelled" } : k
                      ),
                    }
                  : table
              )
            );
          } else if (calledFrom === "room" && selectedRoom) {
            setRooms((prev) =>
              prev.map((room) =>
                room.id === selectedRoom
                  ? {
                      ...room,
                      kots: room.kots.map((k, index) =>
                        index === selectedKOTIndex ? { ...k, status: "cancelled" } : k
                      ),
                    }
                  : room
              )
            );
          }
          notify("success",`KOT for ${selectedTable || selectedRoom} cancelled successfully!`);
          handleDismiss();
        } catch (err) {
          console.error("Error cancelling KOT:", err);
          const errorMessage = err.response?.data?.error || "Failed to cancel KOT";
          message.error(errorMessage);
        }
      },
    });
  };

  const handleRePrintKOT = async (kot) => {
    // Print is handled by the kitchen agent via Socket.IO
    // Frontend just calls the reprint endpoint — no browser dialog
    if (!settings?.kot_settings?.enable_kot_printing) {
     notify("error","KOT printing is disabled in settings.");
      return;
    }
    try {
      await axios.patch(
        `${apiUrl}/pos/kots/${kot.kot_id}/reprint`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
     notify("success","Reprint sent to kitchen printer.");
    } catch (err) {
      console.error("Reprint failed:", err);
      const errorMessage = err.response?.data?.error || "Failed to send reprint";
      message.error(errorMessage);
    }
  };

  const finishCloseKOT = () => {
    setKotItems([]);
    setKotNote("");
    setComplementaryReference("");
    setSelectedKOTIndex(-1);
    setIsComplementary(false);
    setIsAddingNewKOT(false);
    setIsPageOpen(false);

    if (onClose) {
      onClose();
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
    }
  };

  const handleDismiss = () => {
    if (kotItems.length > 0 && selectedKOTIndex === -1) {
      Modal.confirm({
        title: "Discard unsaved KOT?",
        zIndex: 2147483647,
        centered: true,
        okText: "Discard and Close",
        okType: "danger",
        cancelText: "Keep Editing",
        className: "ns-kot-confirm-modal",
        content: (
          <div className="ns-kot-confirm-copy">
            You have unsaved items in this KOT. Closing now will discard the current items and notes.
          </div>
        ),
        onOk: finishCloseKOT,
      });
      return;
    }

    finishCloseKOT();
  };

  const handleSelectKOT = (index) => {
    setSelectedKOTIndex(index);

    if (index === -1) {
      setKotItems([]);
      setKotNote("");
      setComplementaryReference("");
      setIsComplementary(false);
      setWaiter(null);
      setSelectedWaiter(null);
      setIsAddingNewKOT(true);
      return;
    }

    setIsAddingNewKOT(false);
  };

  const itemCount = kotItems.length;
  const totalQuantity = kotItems.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  const estimatedValue = kotItems.reduce(
    (sum, item) => sum + (Number(item.price_standard || 0) * Number(item.qty || 0)),
    0
  );
  const targetLabel = tableNo || selectedTable || selectedRoom || "Select Table / Room";
  const targetType = selectedTable ? "Table" : selectedRoom ? "Room" : calledFrom === "room" ? "Room" : calledFrom === "table" ? "Table" : "KOT";
  const isReviewMode = selectedKOTIndex >= 0;
  const canGenerate = !bootLoading && selectedKOTIndex === -1 && kotItems.length > 0 && !isLoading && (selectedTable || selectedRoom);
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  if (!portalTarget || !isPageOpen) return null;

  // -------- BOOT LOADER --------
  // Show full-screen loader until boot phase is finished. Single condition,
  // matches the user's mental model: "the page is loading" — no in-between
  // half-dead state with toggles that silently do nothing.
  if (bootLoading && menuItems.length === 0) {
    return createPortal(
      <div className="ns-kot-immersive ns-kot-loading-shell">
        <style>{`
          .ns-kot-immersive {
            position: fixed !important;
            inset: 0 !important;
            width: 100vw !important;
            height: 100dvh !important;
            z-index: 2147483647 !important;
            background:
              radial-gradient(circle at 16% 10%, rgba(37, 99, 235, 0.12), transparent 32%),
              radial-gradient(circle at 88% 4%, rgba(34, 197, 94, 0.12), transparent 30%),
              #f8fafc;
            isolation: isolate;
          }

          .ns-kot-loading-card {
            background: rgba(255,255,255,0.94);
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 24px;
            box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
            padding: 30px 36px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 14px;
            color: #64748b;
            font-weight: 900;
            text-align: center;
          }

          .ns-kot-loading-card strong {
            color: #0f172a;
            font-size: 1rem;
          }

          .ns-kot-loading-card small {
            color: #64748b;
            font-size: 0.78rem;
            font-weight: 700;
          }

          .ns-kot-loading-shell {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}</style>

        <div className="ns-kot-loading-card">
          <Spin size="large" />
          <strong>Preparing KOT workspace...</strong>
          <small>Loading menu, categories, waiters and settings</small>
        </div>
      </div>,
      portalTarget
    );
  }

  if (error) {
    return createPortal(
      <div className="ns-kot-immersive ns-kot-error-shell">
        <style>{`
          .ns-kot-immersive {
            position: fixed !important;
            inset: 0 !important;
            width: 100vw !important;
            height: 100dvh !important;
            z-index: 2147483647 !important;
            background: #f8fafc;
            isolation: isolate;
          }

          .ns-kot-error-shell {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
          }

          .ns-kot-error-card {
            max-width: 520px;
            width: 100%;
            background: #ffffff;
            border: 1px solid rgba(220, 38, 38, 0.16);
            border-radius: 24px;
            box-shadow: 0 24px 70px rgba(15, 23, 42, 0.10);
            padding: 26px;
            text-align: center;
          }

          .ns-kot-error-title {
            color: #991b1b;
            font-size: 1.2rem;
            font-weight: 900;
            margin-bottom: 8px;
          }

          .ns-kot-error-text {
            color: #64748b;
            line-height: 1.55;
            margin-bottom: 18px;
          }

          .ns-kot-btn {
            border: 0;
            border-radius: 14px;
            min-height: 42px;
            padding: 9px 14px;
            font-weight: 900;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.18s ease;
            cursor: pointer;
            white-space: nowrap;
          }

          .ns-kot-btn-primary {
            background: #1d4ed8;
            color: #ffffff;
            box-shadow: 0 10px 22px rgba(29, 78, 216, 0.20);
          }
        `}</style>

        <div className="ns-kot-error-card">
          <div className="ns-kot-error-title">Unable to load KOT page</div>
          <div className="ns-kot-error-text">{error}</div>
          <button className="ns-kot-btn ns-kot-btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>,
      portalTarget
    );
  }

  // Combined "block this control" guard. Every input below uses this.
  const isLocked = bootLoading;

  return createPortal(
    <div className="ns-kot-immersive">
      <style>{`
        .ns-kot-immersive {
          position: fixed !important;
          inset: 0 !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100vw !important;
          height: 100dvh !important;
          z-index: 2147483647 !important;
          isolation: isolate;
          background:
            radial-gradient(circle at 12% 4%, rgba(37, 99, 235, 0.10), transparent 28%),
            radial-gradient(circle at 88% 3%, rgba(34, 197, 94, 0.12), transparent 28%),
            #f8fafc;
          overflow: hidden;
          color: #0f172a;
        }

        body.ns-kot-active {
          overflow: hidden !important;
        }

        body.ns-kot-active .ant-select-dropdown,
        body.ns-kot-active .ant-dropdown,
        body.ns-kot-active .ant-popover,
        body.ns-kot-active .ant-modal-root,
        body.ns-kot-active .ant-modal-wrap,
        body.ns-kot-active .ant-modal-mask,
        body.ns-kot-active .ns-kot-react-select__menu-portal {
          z-index: 2147483647 !important;
        }

        .ns-kot-shell {
          height: 100dvh !important;
          width: 100vw !important;
          display: flex;
          flex-direction: column;
          padding: 8px 472px 8px 8px;
          gap: 8px;
        }

        .ns-kot-topbar {
          flex: 0 0 auto;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
          padding: 8px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .ns-kot-title-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .ns-kot-back {
          min-width: 42px;
          height: 42px;
          border-radius: 15px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: #ffffff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
          transition: all 0.18s ease;
          flex: 0 0 auto;
          font-weight: 900;
          cursor: pointer;
        }

        .ns-kot-close-icon {
          width: 42px;
          height: 42px;
          border-radius: 15px;
          border: 1px solid rgba(220, 38, 38, 0.16);
          background: #fff1f2;
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 22px rgba(220, 38, 38, 0.12);
          transition: all 0.18s ease;
          flex: 0 0 auto;
          cursor: pointer;
        }

        .ns-kot-close-icon:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .ns-kot-eyebrow {
          color: #2563eb;
          font-size: 0.7rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 900;
          margin-bottom: 3px;
        }

        .ns-kot-title {
          margin: 0;
          color: #001f5b;
          font-weight: 900;
          font-size: 1.18rem;
          line-height: 1.15;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ns-kot-title span {
          color: #16a34a;
        }

        .ns-kot-workspace {
          flex: 1 1 auto;
          min-height: 0;
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          gap: 8px;
        }

        .ns-kot-card {
          min-height: 0;
          background: rgba(255,255,255,0.96);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }

        .ns-kot-menu-panel,
        .ns-kot-order-panel {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .ns-kot-panel-head {
          flex: 0 0 auto;
          padding: 14px 16px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background:
            radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 28%),
            #ffffff;
        }

        .ns-kot-target-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 12px;
        }

        .ns-kot-menu-scroll {
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
          padding: 14px;
          background: #f8fafc;
        }

        .ns-kot-menu-scroll .pos-menu-container {
          min-height: 100%;
        }

        .ns-kot-menu-scroll .pos-menu-header {
          margin-bottom: 12px !important;
          border-radius: 18px !important;
          box-shadow: none !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          border-top: 0 !important;
          background: #ffffff !important;
          padding: 12px !important;
        }

        .ns-kot-menu-scroll input,
        .ns-kot-menu-scroll .form-control {
          border-radius: 12px !important;
        }

        .ns-kot-order-panel {
          position: fixed;
          top: 6px;
          right: 6px;
          bottom: 6px;
          width: min(458px, 36vw);
          z-index: 5;
          background:
            radial-gradient(circle at top right, rgba(34, 197, 94, 0.09), transparent 28%),
            #ffffff;
          border-top: 4px solid #22c55e;
        }

        .ns-kot-order-head {
          flex: 0 0 auto;
          padding: 13px 16px 12px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background: #ffffff;
        }

        .ns-kot-order-title-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .ns-kot-order-title {
          color: #001f5b;
          font-weight: 900;
          font-size: 1.1rem;
          line-height: 1.25;
          margin: 0;
        }

        .ns-kot-count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 38px;
          height: 32px;
          padding: 0 11px;
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          font-weight: 900;
          font-size: 0.8rem;
          white-space: nowrap;
        }

        .ns-kot-kot-tabs {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
          margin-top: 10px;
        }

        .ns-kot-chip {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 999px;
          background: #f8fafc;
          color: #475569;
          padding: 6px 10px;
          font-size: 0.76rem;
          font-weight: 900;
          cursor: pointer;
          transition: all 0.18s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .ns-kot-chip.active {
          background: #1d4ed8;
          border-color: #1d4ed8;
          color: #ffffff;
        }

        .ns-kot-chip.cancelled {
          background: #fef2f2;
          border-color: #fecaca;
          color: #b91c1c;
        }

        .ns-kot-chip-print {
          background: #ecfeff;
          color: #0e7490;
          border-color: #a5f3fc;
        }

        .ns-kot-service-box {
          margin-top: 12px;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 10px;
          align-items: center;
        }

        .ns-kot-complimentary-ref {
          margin-top: 10px;
          background: #f8fafc;
          border: 1px solid rgba(22, 163, 74, 0.18);
          border-radius: 14px;
          padding: 10px;
        }

        .ns-kot-complimentary-ref label {
          display: block;
          color: #047857;
          font-size: 0.75rem;
          font-weight: 900;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .ns-kot-complimentary-ref .ant-input {
          border-radius: 12px !important;
        }

        .ns-kot-toggle-card {
          min-height: 42px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 14px;
          background: #f8fafc;
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 10px;
        }

        .ns-kot-switch {
          width: 42px;
          height: 22px;
          border-radius: 999px;
          background: #cbd5e1;
          position: relative;
          cursor: pointer;
          transition: background 0.2s ease;
          flex: 0 0 auto;
        }

        .ns-kot-switch.active {
          background: #22c55e;
        }

        .ns-kot-switch::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 16px;
          height: 16px;
          border-radius: 999px;
          background: #ffffff;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.22);
          transition: left 0.2s ease;
        }

        .ns-kot-switch.active::after {
          left: 23px;
        }

        .ns-kot-order-body {
          flex: 1 1 auto;
          min-height: 0;
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          overflow: auto;
          background: #f8fafc;
        }

        .ns-kot-items-card {
          flex: 1 1 auto;
          min-height: 230px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .ns-kot-items-head {
          flex: 0 0 auto;
          padding: 12px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background: #ffffff;
        }

        .ns-kot-items-title {
          color: #0f172a;
          font-weight: 900;
          margin: 0;
          font-size: 0.95rem;
        }

        .ns-kot-items-list {
          flex: 1 1 auto;
          min-height: 160px;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 10px;
        }

        .ns-kot-empty {
          height: 100%;
          min-height: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: #64748b;
          padding: 24px;
        }

        .ns-kot-empty-icon {
          width: 62px;
          height: 62px;
          border-radius: 22px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
        }

        .ns-kot-empty-title {
          color: #0f172a;
          font-weight: 900;
          font-size: 1rem;
          margin-bottom: 4px;
        }

        .ns-kot-line {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.07);
          border-radius: 15px;
          padding: 10px;
          margin-bottom: 8px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
          transition: all 0.18s ease;
        }

        .ns-kot-line:hover {
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
          transform: translateY(-1px);
        }

        .ns-kot-item-name {
          color: #0f172a;
          font-weight: 900;
          line-height: 1.25;
          font-size: 0.88rem;
          word-break: break-word;
        }

        .ns-kot-item-price {
          color: #64748b;
          font-size: 0.73rem;
          margin-top: 4px;
        }

        .ns-kot-qty-controls {
          display: inline-flex;
          align-items: center;
          gap: 7px;
        }

        .ns-kot-icon-btn {
          width: 32px;
          height: 32px;
          border-radius: 11px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: #f8fafc;
          color: #334155;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .ns-kot-icon-btn:hover:not(:disabled) {
          background: #eff6ff;
          color: #1d4ed8;
        }

        .ns-kot-icon-btn:disabled {
          opacity: 0.48;
          cursor: not-allowed;
        }

        .ns-kot-icon-btn.plus {
          background: #ecfdf5;
          color: #047857;
        }

        .ns-kot-icon-btn.delete {
          background: #fef2f2;
          color: #b91c1c;
        }

        .ns-kot-qty-input {
          width: 54px;
          height: 32px;
          border-radius: 11px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          text-align: center;
          color: #0f172a;
          font-weight: 900;
        }

        .ns-kot-note-box {
          flex: 0 0 auto;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          padding: 12px;
        }

        .ns-kot-note-label {
          color: #0f172a;
          font-size: 0.82rem;
          font-weight: 900;
          margin-bottom: 7px;
        }

        .ns-kot-note-box .ant-input {
          border-radius: 12px !important;
        }

        .ns-kot-footer {
          flex: 0 0 auto;
          background: rgba(255,255,255,0.96);
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          padding: 12px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .ns-kot-summary {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.45;
        }

        .ns-kot-summary strong {
          color: #0f172a;
        }

        .ns-kot-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        .ns-kot-btn {
          border: 0;
          border-radius: 14px;
          min-height: 42px;
          padding: 9px 14px;
          font-weight: 900;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.18s ease;
          cursor: pointer;
          white-space: nowrap;
        }

        .ns-kot-btn:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .ns-kot-btn:disabled {
          opacity: 0.58;
          cursor: not-allowed;
        }

        .ns-kot-btn-light {
          background: #f8fafc;
          color: #334155;
          border: 1px solid rgba(15, 23, 42, 0.10);
        }

        .ns-kot-btn-primary {
          background: #1d4ed8;
          color: #ffffff;
          box-shadow: 0 10px 22px rgba(29, 78, 216, 0.20);
        }

        .ns-kot-btn-success {
          background: #16a34a;
          color: #ffffff;
          box-shadow: 0 10px 22px rgba(22, 163, 74, 0.20);
        }

        .ns-kot-btn-danger {
          background: #dc2626;
          color: #ffffff;
          box-shadow: 0 10px 22px rgba(220, 38, 38, 0.16);
        }

        .ns-kot-select .ant-select-selector {
          border-radius: 13px !important;
          min-height: 42px !important;
          display: flex;
          align-items: center;
        }

        .ns-kot-react-select__control {
          border-radius: 13px !important;
          min-height: 42px !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
          box-shadow: none !important;
        }

        .ns-kot-react-select__menu {
          z-index: 2147483647 !important;
        }

        .ns-kot-saving-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483001;
          background: rgba(255,255,255,0.42);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ns-kot-saving-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 24px 70px rgba(15, 23, 42, 0.16);
          padding: 26px 32px;
          display: inline-flex;
          gap: 13px;
          align-items: center;
          color: #64748b;
          font-weight: 900;
        }

        .ns-kot-confirm-modal .ant-modal-content {
          border-radius: 22px !important;
          overflow: hidden;
          box-shadow: 0 26px 70px rgba(15, 23, 42, 0.22) !important;
        }

        .ns-kot-confirm-modal .ant-modal-confirm-title {
          color: #0f172a !important;
          font-weight: 900 !important;
          font-size: 1rem !important;
        }

        .ns-kot-confirm-copy {
          color: #64748b;
          font-weight: 700;
          line-height: 1.55;
          margin-top: 6px;
        }

        .ns-kot-confirm-modal .ant-btn {
          border-radius: 12px !important;
          font-weight: 850 !important;
        }

        .ns-kot-confirm-modal .ant-btn-primary {
          background: #dc2626 !important;
          border-color: #dc2626 !important;
        }

        @media (max-width: 1180px) {
          .ns-kot-shell {
            padding: 8px;
          }

          .ns-kot-order-panel {
            position: static;
            width: auto;
            top: auto;
            right: auto;
            bottom: auto;
          }

          .ns-kot-workspace {
            grid-template-columns: 1fr;
            overflow: auto;
          }

          .ns-kot-menu-panel,
          .ns-kot-order-panel {
            min-height: 560px;
          }

          .ns-kot-immersive {
            overflow: auto;
          }

          .ns-kot-shell {
            min-height: 100dvh;
            height: auto !important;
          }
        }

        @media (max-width: 768px) {
          .ns-kot-shell {
            padding: 8px;
          }

          .ns-kot-topbar {
            align-items: flex-start;
            flex-direction: column;
          }

          .ns-kot-title {
            white-space: normal;
          }

          .ns-kot-close-icon {
            width: 42px;
            height: 42px;
          }

          .ns-kot-target-grid,
          .ns-kot-service-box {
            grid-template-columns: 1fr;
          }

          .ns-kot-line {
            grid-template-columns: 1fr;
          }

          .ns-kot-qty-controls {
            justify-content: flex-end;
          }

          .ns-kot-footer {
            flex-direction: column;
            align-items: stretch;
          }

          .ns-kot-actions {
            justify-content: stretch;
          }

          .ns-kot-actions .ns-kot-btn {
            flex: 1;
          }
        }
      `}</style>

      <div className="ns-kot-shell">
        <header className="ns-kot-topbar">
          <div className="ns-kot-title-wrap">
            <div style={{ minWidth: 0 }}>
              <div className="ns-kot-eyebrow">Kitchen Order Ticket</div>
              <h2 className="ns-kot-title">
                {targetType} KOT for <span>{targetLabel}</span>
              </h2>
            </div>
          </div>

          <button
            type="button"
            className="ns-kot-close-icon"
            onClick={handleDismiss}
            aria-label="Close KOT"
            title="Close KOT"
          >
            <X size={19} />
          </button>
        </header>

        <main className="ns-kot-workspace">
          <section className="ns-kot-card ns-kot-menu-panel">
            {!(selectedTable || selectedRoom) && (
              <div className="ns-kot-panel-head">
                <div className="ns-kot-target-grid">
                  <AntSelect
                    className="ns-kot-select"
                    placeholder="Select Table"
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      setSelectedTable(value);
                      setSelectedRoom(null);
                    }}
                    value={selectedTable}
                    allowClear
                    disabled={isLocked || calledFrom === "room"}
                    size="large"
                    getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                    dropdownStyle={{ zIndex: 2147483647 }}
                  >
                    {tables.map((table) => (
                      <Option key={table.id} value={table.id}>
                        {table.id}
                      </Option>
                    ))}
                  </AntSelect>

                  <AntSelect
                    className="ns-kot-select"
                    placeholder="Select Room"
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      setSelectedRoom(value);
                      setSelectedTable(null);
                    }}
                    value={selectedRoom}
                    allowClear
                    disabled={isLocked || calledFrom === "table"}
                    size="large"
                    getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                    dropdownStyle={{ zIndex: 2147483647 }}
                  >
                    {rooms.map((room) => (
                      <Option key={room.id} value={room.id}>
                        {room.id}
                      </Option>
                    ))}
                  </AntSelect>
                </div>
              </div>
            )}

            <div className="ns-kot-menu-scroll">
              <div className="pos-menu-container">
                <div className="pos-menu-header">
                  {categories.length > 0 ? (
                    <CategoryTabs
                      categories={categories}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "10px", color: "#64748b" }}>
                      No categories available.
                    </div>
                  )}

                  <SearchBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    debouncedSetSearchTerm={debouncedSetSearchTerm}
                    filteredItems={filteredItems}
                    handleAddItem={handleAddItem}
                    highlightedIndex={highlightedIndex}
                    setHighlightedIndex={setHighlightedIndex}
                    showPrice={false}
                  />
                </div>

                <MenuItemGrid
                  filteredItems={filteredItems}
                  billItems={kotItems}
                  handleAddItem={handleAddItem}
                  showPrice={false}
                />
              </div>
            </div>
          </section>

          <aside className="ns-kot-card ns-kot-order-panel">
            <div className="ns-kot-order-head">
              <div className="ns-kot-order-title-row">
                <div>
                  <h3 className="ns-kot-order-title">{isReviewMode ? "Existing KOT" : "Current KOT"}</h3>
                  <div style={{ color: "#64748b", fontSize: "0.78rem", lineHeight: 1.45, marginTop: 3 }}>
                    {isReviewMode ? "Saved KOT is read-only." : "Adjust quantity, then generate."}
                  </div>
                </div>

                <span className="ns-kot-count-badge">{itemCount} Items</span>
              </div>

              {(selectedTable || selectedRoom) && (
                <div className="ns-kot-kot-tabs">
                  <button
                    onClick={() => handleSelectKOT(-1)}
                    className={`ns-kot-chip ${selectedKOTIndex === -1 ? "active" : ""}`}
                    disabled={isLocked}
                  >
                    <PlusCircle size={14} /> New KOT ({displayedKOTs.length + 1})
                  </button>

                  {displayedKOTs.map((kot, index) => (
                    <React.Fragment key={kot.kot_id || index}>
                      <button
                        onClick={() => handleSelectKOT(index)}
                        className={`ns-kot-chip ${kot.status === "cancelled" ? "cancelled" : ""} ${selectedKOTIndex === index ? "active" : ""}`}
                        disabled={isLocked}
                      >
                        KOT {displayedKOTs.length - index}
                      </button>

                      {selectedKOTIndex === index && (
                        <button
                          onClick={() => handleRePrintKOT(kot)}
                          className="ns-kot-chip ns-kot-chip-print"
                          disabled={isLocked}
                        >
                          <Printer size={14} /> Re-print
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {(selectedTable || selectedRoom) && (
                <div className="ns-kot-service-box">
                  <Select
                    classNamePrefix="ns-kot-react-select"
                    options={waiterOptions}
                    value={waiter || selectedWaiter}
                    onChange={(option) => {
                      setWaiter(option);
                      setSelectedWaiter(option);
                    }}
                    placeholder={bootLoading ? "Loading waiters..." : "Select Waiter"}
                    styles={kotReactSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                    isLoading={bootLoading}
                    isClearable
                    isDisabled={isLocked || selectedKOTIndex >= 0}
                  />

                  <label className="ns-kot-toggle-card">
                    <span style={{ color: "#0f172a", fontWeight: 900, fontSize: "0.82rem" }}>
                      Complementary
                    </span>
                    <input
                      type="checkbox"
                      checked={isComplementary}
                      disabled={isLocked || selectedKOTIndex >= 0}
                      onChange={(e) => setIsComplementary(e.target.checked)}
                      style={{ display: "none" }}
                    />
                    <span className={`ns-kot-switch ${isComplementary ? "active" : ""}`} />
                  </label>
                </div>
              )}

              {isComplementary && (
                <div className="ns-kot-complimentary-ref">
                  <label>Complimentary Reference</label>
                  <Input
                    placeholder="Example: Manager approval, owner guest, staff meal, recovery order..."
                    value={complementaryReference}
                    onChange={(e) => setComplementaryReference(e.target.value)}
                    disabled={isLocked || selectedKOTIndex >= 0}
                  />
                </div>
              )}
            </div>

            <div className="ns-kot-order-body">
              <div className="ns-kot-items-card">
                <div className="ns-kot-items-head">
                  <h4 className="ns-kot-items-title">KOT Items</h4>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 7, borderRadius: 999,
                    background: "#ffffff", border: "1px solid rgba(15, 23, 42, 0.08)",
                    color: "#334155", padding: "7px 11px", fontSize: "0.78rem", fontWeight: 900,
                    whiteSpace: "nowrap", boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)"
                  }}>
                    {Number(totalQuantity).toFixed(totalQuantity % 1 ? 2 : 0)} qty
                  </span>
                </div>

                <div className="ns-kot-items-list">
                  {kotItems.length === 0 ? (
                    <div className="ns-kot-empty">
                      <div>
                        <div className="ns-kot-empty-icon">
                          <PlusCircle size={34} />
                        </div>
                        <div className="ns-kot-empty-title">Add items to generate KOT</div>
                        <div>Tap any menu item on the left. Quantity controls and Generate KOT will appear here.</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {kotItems.map((item) => (
                        <div key={item.menu_id} className="ns-kot-line">
                          <div style={{ minWidth: 0 }}>
                            <div className="ns-kot-item-name">{item.name}</div>
                            <div className="ns-kot-item-price">
                              ₹{(Number(item.price_standard) || 0).toFixed(2)} each
                            </div>
                          </div>

                          <div className="ns-kot-qty-controls">
                            <button
                              type="button"
                              className="ns-kot-icon-btn"
                              onClick={() => handleQuantityChange(item.menu_id, -1)}
                              disabled={isLocked || item.qty <= 0.1 || selectedKOTIndex >= 0}
                              aria-label="Decrease quantity"
                            >
                              <Minus size={14} />
                            </button>

                            <input
                              className="ns-kot-qty-input"
                              type="number"
                              value={item.qty}
                              onChange={(e) => {
                                const newQty = parseFloat(e.target.value) || 0;
                                if (newQty >= 0) {
                                  handleQuantityChange(item.menu_id, newQty - item.qty);
                                }
                              }}
                              step="0.01"
                              min="0"
                              disabled={isLocked || selectedKOTIndex >= 0}
                            />

                            <button
                              type="button"
                              className="ns-kot-icon-btn plus"
                              onClick={() => handleQuantityChange(item.menu_id, 1)}
                              disabled={isLocked || selectedKOTIndex >= 0}
                              aria-label="Increase quantity"
                            >
                              <Plus size={14} />
                            </button>

                            <button
                              type="button"
                              className="ns-kot-icon-btn delete"
                              onClick={() => handleRemoveItem(item.menu_id)}
                              disabled={isLocked || selectedKOTIndex >= 0}
                              aria-label="Remove item"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {settings?.kot_settings?.print_item_notes_in_kot && selectedKOTIndex === -1 && kotItems.length > 0 && (
                <div className="ns-kot-note-box">
                  <div className="ns-kot-note-label">KOT Note</div>
                  <Input.TextArea
                    placeholder="Add note for this KOT, e.g. spicy, serve first, urgent order..."
                    value={kotNote}
                    onChange={(e) => setKotNote(e.target.value)}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    disabled={isLocked}
                  />
                </div>
              )}
            </div>

            {(selectedTable || selectedRoom) && (
              <div className="ns-kot-footer">
                <div className="ns-kot-summary">
                  <div><strong>{Number(totalQuantity).toFixed(totalQuantity % 1 ? 2 : 0)}</strong> qty across <strong>{itemCount}</strong> item type{itemCount === 1 ? "" : "s"}</div>
                  <div>Estimated item value: <strong>₹{estimatedValue.toFixed(2)}</strong></div>
                </div>

                <div className="ns-kot-actions">
                  <button onClick={handleDismiss} className="ns-kot-btn ns-kot-btn-light">
                    <ArrowLeft size={18} /> Back
                  </button>

                  {selectedKOTIndex >= 0 && displayedKOTs[selectedKOTIndex]?.status !== "cancelled" && (
                    <button
                      onClick={() => handleCancelKOT(displayedKOTs[selectedKOTIndex].kot_id)}
                      className="ns-kot-btn ns-kot-btn-danger"
                      disabled={isLocked}
                    >
                      Cancel KOT
                    </button>
                  )}

                  {selectedKOTIndex === -1 && (
                    <button
                      onClick={() =>
                        handleGenerateKOT({
                          kot_number: `KOT-${Date.now()}`,
                          table_id: calledFrom === "table" ? selectedTable : null,
                          room_id: calledFrom === "room" ? selectedRoom : null,
                          items: kotItems,
                          order_time: moment().format("YYYY-MM-DD HH:mm:ss"),
                          is_complementary: isComplementary,
                          complementary_reference: isComplementary ? complementaryReference : undefined,
                          waiter_id: waiter ? waiter.value : undefined,
                          waiter_name: waiter ? waiter.label : undefined,
                          notes: settings?.kot_settings?.print_item_notes_in_kot ? kotNote : undefined,
                        })
                      }
                      disabled={!canGenerate}
                      className="ns-kot-btn ns-kot-btn-success"
                    >
                      {isLoading ? <Spin size="small" /> : <Check size={18} />}
                      Generate KOT
                    </button>
                  )}
                </div>
              </div>
            )}
          </aside>
        </main>
      </div>

      {isLoading && (
        <div className="ns-kot-saving-overlay">
          <div className="ns-kot-saving-card">
            <Spin />
            <span>Generating KOT...</span>
          </div>
        </div>
      )}

      {/* Print iframe removed — printing handled by kitchen agent via Socket.IO */}
    </div>,
    portalTarget
  );
};

export default KOTGenerationPage;